/**
 * lens_stars — what a black hole's lens needs to keep the catalogue stars' LIGHT (space look, second pass, 2026-10-08).
 *
 * THE FINDING. The space star field draws a star as a Gaussian no narrower than the pixel's footprint and STRETCHES
 * its peak (x^2.8 under the catalogue's 99th percentile — the magnitude stretch). Along a bent direction the
 * footprint is the LENSED beam, so the stretch read the lens's demagnification as "a dimmer star": a beam twice as
 * wide drew a star at 2^-3.6 = 8 % of its light, four times as wide at 0.7 % — the dark, star-poor zone round a hole
 * (measured in the bed: the stars' mean inside 15° fell to a few per cent of the same sky un-lensed). Past that the
 * window at a star's stored reach cut what was left.
 *
 * THE RULE (space_stars `floor0`, black_hole_look `points`):
 *   - THE DISPLAY'S STRETCH, THE LENS'S SHAPE. How bright a star is drawn is decided at the footprint the SAME pixel
 *     would have with no hole (`floor0` — exactly what the plain sky uses there); the lensed footprint only shapes the
 *     Gaussian, at the same flux. The lens then magnifies that flux as it should (an arc near the ring, a faint wide
 *     glow where the sky is squeezed), and far from the hole the two footprints are one: the plain sky, unchanged.
 *   - WHAT THE WINDOW CUTS COMES BACK AS THE MEAN. A squeezed star is wider than its stored reach; the share of its
 *     light the reach window cuts — `lost(f) − lost(f0)`, never negative — is added back as the LOCAL mean of the
 *     field (`mean0(f0)` × a 64 × 32 density map of the catalogue): smooth, so nothing sparkles.
 *
 * Pure: no three, no DOM. The tables are built once per catalogue (~0.2 s of sums) and mirrored in the shader by two small
 * textures; `verify:black-hole` §10 pins the numbers, the bed's `stars` subject the picture.
 */
import type { StarCatalog } from '../sky/star_catalog.js';

/** The star field's own footprint clamp (space_stars: `clamp(length(fwidth(d)), 0.00035, 0.02)`). */
export const LENS_F_MIN = 0.00035;
export const LENS_F_MAX = 0.02;
/** The footprint table: log-spaced over [LENS_F_MIN, LENS_F_MAX]. */
export const LENS_LUT_N = 32;
/** The density map (equirect, three's equirectUV frame) and its smoothing on the sphere. */
export const LENS_DENS_W = 64;
export const LENS_DENS_H = 32;
export const LENS_DENS_SIGMA = (6 * Math.PI) / 180;
/** The footprint the density map's weights are taken at (the map is a RELATIVE distribution; it barely depends on it). */
export const LENS_F_REF = 0.002;
/** A star's Gaussian floor is this share of the footprint (space_stars `pxFloor`). */
export const LENS_FLOOR_SHARE = 0.75;

export interface LensStarOpts {
  /** The catalogue's 99th-percentile peak (the stretch's reference). */
  p99: number;
  shrink: number; gamma: number; gammaHi: number; target: number; halo: number;
  /** The stored reach of a star (star_catalog.starReach). */
  reachOf(sigma: number, flux: number): number;
}

export interface LensStarTables {
  /** LENS_LUT_N × 2: [mean0, lost] per footprint. */
  lut: Float32Array;
  /** LENS_DENS_W × LENS_DENS_H × 3: the local drawn radiance ÷ the sphere's mean, per channel. */
  dens: Float32Array;
  /** The sphere-mean radiance the plain sky draws for a WHITE star field at footprint f (scene units). */
  mean0(f: number): number;
  /** The share of a star's core the reach window cuts at footprint f. */
  lost(f: number): number;
  /** The density map at a unit direction (bilinear, the shader's lookup). */
  density(x: number, y: number, z: number): [number, number, number];
}

/** The table coordinate of a footprint, 0..1 over [LENS_F_MIN, LENS_F_MAX] (the shader's `lutU`). */
export function lensFootT(f: number): number {
  const c = Math.min(LENS_F_MAX, Math.max(LENS_F_MIN, Number.isFinite(f) ? f : LENS_F_MIN));
  return Math.log(c / LENS_F_MIN) / Math.log(LENS_F_MAX / LENS_F_MIN);
}
/** The un-lensed footprint of a pixel at the view's centre, in the star field's own measure (|fwidth| of the ray). */
export function centreFootprint(fovYRad: number, heightPx: number): number {
  const px = (2 * Math.tan(Math.max(1e-4, fovYRad) / 2)) / Math.max(1, heightPx);
  return Math.min(LENS_F_MAX, Math.max(LENS_F_MIN, px * Math.SQRT2));
}

const stretch = (peak: number, o: LensStarOpts): number => {
  const x = Math.max(0, peak) / o.p99;
  return o.target * Math.pow(x, x < 1 ? o.gamma : o.gammaHi);
};
/** The flux the plain sky draws for one star at footprint f: its stretched peak × the Gaussian's area + the halo's. */
export function drawnFlux(sigma: number, flux: number, f: number, o: LensStarOpts): number {
  const s = Math.max(sigma * o.shrink, LENS_FLOOR_SHARE * f);
  const L = stretch(flux / (2 * Math.PI * s * s), o);
  return L * 2 * Math.PI * (s * s + o.halo * sigma * sigma);
}
/** The share of a Gaussian (σ) the star field's window keeps: ∫ e^(−r²/2σ²)·(1 − smoothstep(0.7R, R, r))·r dr ÷ σ². */
export function windowKept(sigma: number, reach: number): number {
  const n = 16, top = Math.min(reach, 6 * sigma), dr = top / n;
  let s = 0;
  for (let i = 0; i < n; i++) {
    const r = (i + 0.5) * dr;
    const t = Math.min(1, Math.max(0, (r - 0.7 * reach) / (0.3 * reach)));
    s += Math.exp(-(r * r) / (2 * sigma * sigma)) * (1 - t * t * (3 - 2 * t)) * r * dr;
  }
  return Math.min(1, s / (sigma * sigma));
}

/** three's equirectUV of a unit direction: u = atan2(z, x)/2π + ½, v = asin(y)/π + ½. */
export function equirectUvOf(x: number, y: number, z: number): [number, number] {
  return [Math.atan2(z, x) / (2 * Math.PI) + 0.5, Math.asin(Math.min(1, Math.max(-1, y))) / Math.PI + 0.5];
}

export function buildLensStarTables(cat: StarCatalog, o: LensStarOpts): LensStarTables {
  const N = LENS_LUT_N, W = LENS_DENS_W, H = LENS_DENS_H;
  const lut = new Float32Array(N * 2), dens = new Float32Array(W * H * 3);
  const ok = cat.count > 0 && o.p99 > 0;
  const fAt = (i: number): number => LENS_F_MIN * Math.pow(LENS_F_MAX / LENS_F_MIN, i / (N - 1));
  if (ok) {
    // Sums over a deterministic SAMPLE of the catalogue (it is flux-sorted, so a stride samples every brightness alike):
    // mean0(f) over ~30,000 stars, lost(f) over ~5,000 weighted by each core's drawn flux at the reference footprint.
    const sMean = Math.max(1, Math.floor(cat.count / 30_000)), stride = Math.max(1, Math.floor(cat.count / 5_000));
    for (let k = 0; k < N; k++) {
      const f = fAt(k);
      let sum = 0, used = 0, w = 0, kept = 0;
      for (let i = 0; i < cat.count; i += sMean) { sum += drawnFlux(cat.sigma[i], cat.flux[i], f, o); used++; }
      sum *= cat.count / Math.max(1, used);
      for (let i = 0; i < cat.count; i += stride) {
        const sg = cat.sigma[i], fl = cat.flux[i];
        const s0 = Math.max(sg * o.shrink, LENS_FLOOR_SHARE * LENS_F_REF);
        const wi = stretch(fl / (2 * Math.PI * s0 * s0), o) * s0 * s0;
        if (!(wi > 0)) continue;
        w += wi; kept += wi * windowKept(Math.max(sg * o.shrink, LENS_FLOOR_SHARE * f), o.reachOf(sg, fl));
      }
      lut[k * 2] = sum / (4 * Math.PI);
      lut[k * 2 + 1] = w > 0 ? Math.min(1, Math.max(0, 1 - kept / w)) : 0;
    }
    for (let k = 1; k < N; k++) lut[k * 2 + 1] = Math.max(lut[k * 2 + 1], lut[(k - 1) * 2 + 1]); // the cut never shrinks as the beam widens
    // the density map: bin by direction, smooth on the sphere, divide by the sphere's mean
    const bin = new Float64Array(W * H * 3);
    let total = 0;
    for (let i = 0; i < cat.count; i++) {
      const x = cat.dir[i * 3], y = cat.dir[i * 3 + 1], z = cat.dir[i * 3 + 2];
      const [u, v] = equirectUvOf(x, y, z);
      const bi = Math.min(W - 1, Math.max(0, Math.floor(u * W))), bj = Math.min(H - 1, Math.max(0, Math.floor(v * H)));
      const F = drawnFlux(cat.sigma[i], cat.flux[i], LENS_F_REF, o);
      total += F;
      for (let c = 0; c < 3; c++) bin[(bj * W + bi) * 3 + c] += F * cat.color[i * 3 + c];
    }
    const cx = new Float64Array(W * H), cy = new Float64Array(W * H), cz = new Float64Array(W * H), om = new Float64Array(W * H);
    for (let j = 0; j < H; j++) for (let i = 0; i < W; i++) {
      const lon = ((i + 0.5) / W - 0.5) * 2 * Math.PI, lat = ((j + 0.5) / H - 0.5) * Math.PI, q = j * W + i;
      cx[q] = Math.cos(lat) * Math.cos(lon); cy[q] = Math.sin(lat); cz[q] = Math.cos(lat) * Math.sin(lon);
      om[q] = (2 * Math.PI / W) * (Math.PI / H) * Math.cos(lat);
    }
    const k2 = 1 / (2 * LENS_DENS_SIGMA * LENS_DENS_SIGMA), cut = Math.cos(4 * LENS_DENS_SIGMA);
    const mean = total / (4 * Math.PI);
    for (let a = 0; a < W * H; a++) {
      let r = 0, g = 0, b = 0, wsum = 0;
      for (let q = 0; q < W * H; q++) {
        const c = cx[a] * cx[q] + cy[a] * cy[q] + cz[a] * cz[q];
        if (c < cut) continue;
        const ang2 = 2 * (1 - c);                              // chord², ≈ angle² this close
        const wq = Math.exp(-ang2 * k2);
        r += bin[q * 3] * wq; g += bin[q * 3 + 1] * wq; b += bin[q * 3 + 2] * wq; wsum += om[q] * wq;
      }
      const d = wsum > 0 && mean > 0 ? 1 / (wsum * mean) : 0;  // flux ÷ solid angle ÷ the sphere's mean
      dens[a * 3] = r * d; dens[a * 3 + 1] = g * d; dens[a * 3 + 2] = b * d;
    }
  }
  const at = (f: number, c: number): number => {
    const t = lensFootT(f) * (N - 1), i = Math.min(N - 2, Math.floor(t)), k = t - i;
    return lut[i * 2 + c] * (1 - k) + lut[(i + 1) * 2 + c] * k;
  };
  const density = (x: number, y: number, z: number): [number, number, number] => {
    const [u, v] = equirectUvOf(x, y, z);
    const fx = u * W - 0.5, fy = Math.min(H - 1, Math.max(0, v * H - 0.5));
    const i0 = Math.floor(fx), j0 = Math.min(H - 2, Math.floor(fy)), kx = fx - i0, ky = fy - j0;
    const px = (i: number, j: number, c: number): number => dens[(j * W + (((i % W) + W) % W)) * 3 + c];
    const out: [number, number, number] = [0, 0, 0];
    for (let c = 0; c < 3; c++) out[c] = (px(i0, j0, c) * (1 - kx) + px(i0 + 1, j0, c) * kx) * (1 - ky) + (px(i0, j0 + 1, c) * (1 - kx) + px(i0 + 1, j0 + 1, c) * kx) * ky;
    return out;
  };
  return { lut, dens, mean0: (f) => at(f, 0), lost: (f) => at(f, 1), density };
}

/** What the lens puts where a beam spans more sky than any filter resolves: the plain sky's own mean at this display. */
export function lensWideMean(t: LensStarTables, f0: number): number {
  return t.mean0(f0) * (1 - t.lost(f0));
}
